import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../config/api';

const TAG_COLORS = [
  { name: 'Czerwony', value: '#FF6347B3' },
  { name: 'Niebieski', value: '#4169E1B3' },
  { name: 'Zielony', value: '#3CB371B3' },
  { name: 'Żółty', value: '#FFD700B3' },
  { name: 'Fioletowy', value: '#9370DBB3' },
  { name: 'Pomarańczowy', value: '#FFA500B3' },
  { name: 'Różowy', value: '#FFB6C1B3' },
  { name: 'Cyan', value: '#00FFFFB3' },
  { name: 'Brązowy', value: '#8B4513B3' },
  { name: 'Szary', value: '#808080B3' }
];

const ColorPickerPopover = ({ isOpen, onClose, selectedColor, onSelectColor, buttonRef }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="absolute z-50 bg-white rounded-lg shadow-lg p-3 w-48"
      style={{ 
        bottom: '100%', 
        left: '-300%',
        marginBottom: '5px',
      }}
    >
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-xs font-medium">Wybierz kolor</h3>
        <button
          onClick={onClose}
          className="text-neutral-500 hover:text-neutral-700"
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="grid grid-cols-5 gap-1">
        {TAG_COLORS.map(color => (
          <button
            key={color.value}
            type="button"
            onClick={() => {
              onSelectColor(color.value);
              onClose();
            }}
            className={`w-6 h-6 rounded-full border ${
              selectedColor === color.value ? 'ring-2 ring-primary-500' : ''
            }`}
            style={{ backgroundColor: color.value }}
            title={color.name}
          />
        ))}
      </div>
    </div>
  );
};

const ManageTagsModal = ({ isOpen, onClose, listId, onTagDeleted }) => {
  const [tags, setTags] = useState([]);
  const [newTagName, setNewTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState(TAG_COLORS[0].value);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const colorButtonRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchTags();
    }
  }, [isOpen, listId]);

  // Obsługa kliknięcia poza popoverem
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isColorPickerOpen && colorButtonRef.current && !colorButtonRef.current.contains(event.target)) {
        setIsColorPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isColorPickerOpen]);

  const fetchTags = async () => {
    try {
      setIsLoading(true);
      const data = await api.getTagsByWordList(listId);
      setTags(data);
    } catch (err) {
      setError('Nie udało się pobrać tagów');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!newTagName.trim()) {
      setError('Nazwa tagu nie może być pusta');
      return;
    }

    try {
      setIsLoading(true);
      const newTag = await api.createTag({
        name: newTagName.trim(),
        color: selectedColor,
        wordListId: listId
      });
      setTags([...tags, newTag]);
      setNewTagName('');
      setError('');
    } catch (err) {
      setError(err.message || 'Nie udało się utworzyć tagu');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteTag = async (tagId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten tag?')) {
      return;
    }

    try {
      setIsLoading(true);
      await api.deleteTag(tagId);
      setTags(tags.filter(tag => tag.id !== tagId));
      if (onTagDeleted) {
        onTagDeleted(tagId);
      }
    } catch (err) {
      setError('Nie udało się usunąć tagu');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="p-4">
          <h2 className="text-sm font-semibold mb-4">Zarządzaj tagami</h2>

          {error && (
            <div className="mb-4 p-3 text-sm bg-red-50 text-red-600 rounded-md">
              {error}
            </div>
          )}

          <form onSubmit={handleAddTag} className="mb-4">
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Nazwa tagu"
                className="flex-1 px-3 py-2 text-xs border border-neutral-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              <div className="relative flex items-center justify-center" ref={colorButtonRef}>
                <button
                  type="button"
                  onClick={() => setIsColorPickerOpen(!isColorPickerOpen)}
                  className="w-8 h-8 rounded-full border flex-shrink-0"
                  style={{ backgroundColor: selectedColor }} 
                />
                <ColorPickerPopover
                  isOpen={isColorPickerOpen}
                  onClose={() => setIsColorPickerOpen(false)}
                  selectedColor={selectedColor}
                  onSelectColor={setSelectedColor}
                  buttonRef={colorButtonRef}
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Dodaj
              </button>
            </div>
          </form>

          <div className="space-y-2">
            {isLoading && tags.length === 0 ? (
              <div className="text-center text-neutral-500 text-sm py-4">
                Ładowanie tagów...
              </div>
            ) : tags.length === 0 ? (
              <div className="text-center text-neutral-500 text-sm py-4">
                Brak tagów na tej liście
              </div>
            ) : (
              tags.map(tag => (
                <div
                  key={tag.id}
                  className="flex items-center justify-between p-2 bg-neutral-50 rounded-md"
                >
                  <div className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="text-sm">{tag.name}</span>
                  </div>
                  <button
                    onClick={() => handleDeleteTag(tag.id)}
                    className="text-neutral-400 hover:text-neutral-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageTagsModal; 